import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { Modal } from '@/shared/ui/Modal'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { SlideOver } from '@/shared/ui/SlideOver'
import { FilterBar } from '@/shared/ui/FilterBar'
import { Edit, Loader2, Search, X, Percent, GraduationCap, ChevronLeft, ChevronRight, Users, CheckCircle2, UserRound } from 'lucide-react'
import { useDebounce } from '@/shared/hooks'
import { useVendorCommissions, useTrainerCommissions, useUpdateCommission, useSchool } from '@/api/hooks'
import { schoolService } from '@/api/services/schoolService'
import { cn, localized } from '@/shared/utils'

const TABS = [
  { labelKey: 'trainer',     path: '/dashboard/commission/trainer',             key: 'trainer' },
  { labelKey: 'certTrainer', path: '/dashboard/commission/certificate-trainer', key: 'cert-trainer' },
  { labelKey: 'certDrivys',  path: '/dashboard/commission/certificate-drivys',  key: 'cert-drivys' },
]

interface Row {
  id: number
  // names
  name?: string; trainer_name?: string; vendor_name?: string; school_name?: string
  // ids
  vendor_id?: number; trainer_id?: number; vendor_trainer_mapping_id?: number
  // commission values
  vendor_commission_in_percentage?: number
  certificate_commission_in_percentage?: number
  trainer_certificate_commission_in_percentage?: number
  commission?: number
  // bounds (vendor cert view)
  min_commission?: number; max_commission?: number
}

// Plain percentage read-out for a table cell, with the allowed range as a caption underneath.
function CommissionCell({ value, min, max, tone }: { value: number; min?: number; max?: number; tone: 'brand' | 'indigo' }) {
  const { t } = useTranslation()
  const text = tone === 'brand' ? 'text-brand-700' : 'text-indigo-700'
  return (
    <div>
      <span className={cn('font-bold text-sm tabular-nums', text)}>{value}%</span>
      {(min != null || max != null) && (
        <div className="text-[10px] text-gray-400 mt-0.5">{t('commissionPage.schoolView.allowedRange', { min: min ?? 0, max: max ?? 100 })}</div>
      )}
    </div>
  )
}

// Generic placeholder avatar for trainers with no uploaded photo — a neutral silhouette
// rather than colored initials, so missing photos read as "no photo yet" not as data.
function TrainerAvatar({ photo, name, size = 36 }: { photo?: string | null; name?: string; size?: number }) {
  if (photo) {
    return <img src={photo} alt={name} style={{ width: size, height: size }} className="rounded-full object-cover flex-shrink-0" />
  }
  return (
    <div style={{ width: size, height: size }} className="rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <UserRound size={size * 0.6} className="text-gray-400" />
    </div>
  )
}

// A percentage slider paired with a numeric input — easier to reason about than a bare
// number field, and the live gauge underneath shows where the new value lands in-range.
function CommissionSliderField({ icon, label, value, onChange, min, max, tone }: {
  icon: React.ReactNode; label: string; value: string; onChange: (v: string) => void
  min?: number; max?: number; tone: 'brand' | 'indigo'
}) {
  const { t } = useTranslation()
  const lo = min ?? 0
  const hi = max ?? 100
  const num = value === '' ? lo : Number(value)
  const accent = tone === 'brand' ? 'accent-brand-500' : 'accent-indigo-500'
  return (
    <div>
      <label className={cn('text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1.5 mb-2', tone === 'brand' ? 'text-brand-700' : 'text-indigo-700')}>
        {icon} {label}
      </label>
      <div className="flex items-center gap-3">
        <input
          type="range" min={lo} max={hi} value={num}
          onChange={e => onChange(e.target.value)}
          className={cn('flex-1 h-1.5 rounded-full cursor-pointer', accent)}
        />
        <div className="relative flex-shrink-0">
          <input
            type="number" min={lo} max={hi} value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="0"
            className="w-20 ps-3 pe-6 py-1.5 text-sm text-end border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          <span className="absolute end-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">%</span>
        </div>
      </div>
      <div className="flex justify-between text-[11px] text-gray-400 mt-1">
        <span>{t('commissionPage.schoolView.minLabel', { value: lo })}</span><span>{t('commissionPage.schoolView.maxLabel', { value: hi })}</span>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// School-admin commission — combined Session + Certificate commission per trainer
// API: admin/trainers/get-school-trainer-commission-list
// ══════════════════════════════════════════════════════════════════════════════
function SchoolCommissionView() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch]   = useState('')
  const [page, setPage]       = useState(1)
  const perPage = 15
  const debounced = useDebounce(search)

  const q = useTrainerCommissions({ page, limit: perPage, search: debounced || undefined })
  const update = useUpdateCommission()

  const rows: any[] = (q.data as any)?.data ?? []
  const total       = (q.data as any)?.total ?? rows.length
  const lastPage    = (q.data as any)?.last_page ?? Math.max(1, Math.ceil(total / perPage))

  const [editing, setEditing] = useState<any | null>(null)
  const [session, setSession] = useState('')
  const [cert,    setCert]    = useState('')
  const [error,   setError]   = useState('')
  const [confirmSave, setConfirmSave] = useState(false)

  // Vendor-supplied bounds for the trainer being edited
  const sMin = editing?.vendor?.min_commision, sMax = editing?.vendor?.max_commision
  const cMin = editing?.vendor?.certificate_min_commision, cMax = editing?.vendor?.certificate_max_commision

  const openEdit = (r: any) => {
    setEditing(r)
    setSession(String(r.vendor_commission_in_percentage ?? ''))
    setCert(String(r.certificate_commission_in_percentage ?? ''))
    setError('')
    setConfirmSave(false)
  }

  const outOfRange = (v: string, min?: number, max?: number) => {
    if (v === '') return false
    const n = Number(v)
    if (n < 0 || n > 100) return true
    if (min != null && n < Number(min)) return true
    if (max != null && n > Number(max)) return true
    return false
  }

  const validateLive = (nextSession: string, nextCert: string) => {
    if (outOfRange(nextSession, sMin, sMax)) { setError(t('commissionPage.schoolView.sessionRangeError', { min: sMin ?? 0, max: sMax ?? 100 })); return }
    if (outOfRange(nextCert, cMin, cMax))    { setError(t('commissionPage.schoolView.certRangeError', { min: cMin ?? 0, max: cMax ?? 100 })); return }
    setError('')
  }

  // Validates, then asks for confirmation — commission changes are limited to
  // once every 20 days per trainer, so this isn't something to submit by accident.
  const requestSave = () => {
    if (!editing) return
    if (outOfRange(session, sMin, sMax)) { setError(t('commissionPage.schoolView.sessionRangeError', { min: sMin ?? 0, max: sMax ?? 100 })); return }
    if (outOfRange(cert, cMin, cMax))    { setError(t('commissionPage.schoolView.certRangeError', { min: cMin ?? 0, max: cMax ?? 100 })); return }
    setConfirmSave(true)
  }

  const handleSave = async () => {
    if (!editing) return

    const origSession = String(editing.vendor_commission_in_percentage ?? '')
    const origCert    = String(editing.certificate_commission_in_percentage ?? '')

    // The endpoint takes one commission field per call, keyed by mapping_id.
    if (session !== '' && session !== origSession) {
      await update.mutateAsync({ mapping_id: editing.id, vendor_commission_in_percentage: session })
    }
    if (cert !== '' && cert !== origCert) {
      await update.mutateAsync({ mapping_id: editing.id, certificate_commission_in_percentage: cert })
    }
    setConfirmSave(false)
    setEditing(null)
  }

  const pageNums = (() => {
    let s = Math.max(1, page - 2); if (s + 4 > lastPage) s = Math.max(1, lastPage - 4)
    return Array.from({ length: Math.min(5, lastPage) }, (_, i) => s + i).filter(p => p <= lastPage)
  })()

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('commissionPage.schoolView.title')}
        subtitle={t('commissionPage.schoolView.subtitle')}
        breadcrumbs={[{ label: t('commissionPage.breadcrumb.dashboard'), to: '/school/overview' }, { label: t('commissionPage.breadcrumb.commission') }]}
      />

      {/* Search + summary */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 border border-gray-200 rounded-xl px-3 py-2.5 bg-white flex-1 min-w-56 max-w-sm">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder={t('commissionPage.schoolView.searchPlaceholder')}
            className="text-sm text-gray-700 placeholder-gray-400 focus:outline-none flex-1 bg-transparent min-w-0" />
          {search && <button onClick={() => setSearch('')}><X size={13} className="text-gray-300 hover:text-gray-500" /></button>}
        </div>
        {!q.isLoading && total > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-xl px-3 py-2.5">
            <Users size={13} className="text-brand-500" />
            <span className="font-bold text-gray-900">{t('commissionPage.schoolView.trainerCount', { count: total })}</span>
          </div>
        )}
      </div>

      <Card padding={false} className="rounded-2xl overflow-hidden">
        {q.isError && <p className="px-5 py-4 text-sm text-red-500">{t('commissionPage.schoolView.loadFailed')}</p>}
        {q.isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand-500" size={22} /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <Users size={24} className="text-gray-300" />
            </div>
            <p className="text-sm font-semibold text-gray-500">{t('commissionPage.schoolView.empty')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th>{t('commissionPage.schoolView.columnTrainer')}</th>
                  <th><span className="inline-flex items-center gap-1.5"><Percent size={12} /> {t('commissionPage.schoolView.columnTrainerCommission')}</span></th>
                  <th><span className="inline-flex items-center gap-1.5"><GraduationCap size={12} /> {t('commissionPage.schoolView.columnCertificateCommission')}</span></th>
                  <th className="text-end">{t('commissionPage.schoolView.columnAction')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any) => {
                  const tName = localized(r.user, 'name', i18n.language)
                  const tId   = r.user?.id ?? r.user_id
                  const photo = r.user?.photo_url
                  return (
                    <tr key={r.id ?? tId}>
                      <td>
                        <div className="flex items-center gap-3 min-w-0">
                          <TrainerAvatar photo={photo} name={tName} size={36} />
                          <div className="min-w-0">
                            <button onClick={() => tId && navigate(`/school/trainer/${tId}`)}
                              className="font-semibold text-gray-900 hover:text-brand-600 hover:underline text-start block truncate max-w-[180px]">
                              {tName}
                            </button>
                            <div className="text-[12px] text-gray-400">{t('commissionPage.schoolView.idPrefix', { id: tId })}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <CommissionCell tone="brand" value={r.vendor_commission_in_percentage ?? 0}
                          min={r.vendor?.min_commision} max={r.vendor?.max_commision} />
                      </td>
                      <td>
                        <CommissionCell tone="indigo" value={r.certificate_commission_in_percentage ?? 0}
                          min={r.vendor?.certificate_min_commision} max={r.vendor?.certificate_max_commision} />
                      </td>
                      <td className="text-end">
                        <button onClick={() => openEdit(r)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-50 text-brand-600 hover:bg-brand-100 text-xs font-semibold transition-colors">
                          <Edit size={13} /> {t('commissionPage.schoolView.edit')}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!q.isLoading && total > 0 && lastPage > 1 && (
          <div className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap border-t border-gray-100">
            <span className="text-xs text-gray-500">{t('commissionPage.schoolView.paginationRange', { start: (page-1)*perPage+1, end: Math.min(page*perPage, total), total })}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(Math.max(1, page-1))} disabled={page<=1}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40"><ChevronLeft size={14} className="rtl:rotate-180" /></button>
              {pageNums.map(p => (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg border text-xs font-semibold ${p===page ? 'bg-brand-500 text-white border-brand-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{p}</button>
              ))}
              <button onClick={() => setPage(Math.min(lastPage, page+1))} disabled={page>=lastPage}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40"><ChevronRight size={14} className="rtl:rotate-180" /></button>
            </div>
          </div>
        )}
      </Card>

      {/* Edit modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={t('commissionPage.schoolView.modalTitle')} size="sm">
        {editing && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
              <TrainerAvatar photo={editing.user?.photo_url} name={localized(editing.user, 'name', i18n.language)} size={44} />
              <div className="min-w-0">
                <div className="text-sm font-bold text-gray-900 truncate">{localized(editing.user, 'name', i18n.language)}</div>
                <div className="text-xs text-gray-400">{t('commissionPage.schoolView.idPrefix', { id: editing.user?.id ?? editing.user_id })}</div>
              </div>
            </div>

            <CommissionSliderField
              icon={<Percent size={12} />} label={t('commissionPage.schoolView.sliderTrainerCommission')} tone="brand"
              value={session} onChange={v => { setSession(v); validateLive(v, cert) }}
              min={sMin} max={sMax}
            />
            <CommissionSliderField
              icon={<GraduationCap size={12} />} label={t('commissionPage.schoolView.sliderTrainerCertCommission')} tone="indigo"
              value={cert} onChange={v => { setCert(v); validateLive(session, v) }}
              min={cMin} max={cMax}
            />

            {error ? (
              <p className="text-xs text-red-500">{error}</p>
            ) : (
              <p className="flex items-center gap-1.5 text-xs text-green-600">
                <CheckCircle2 size={13} /> {t('commissionPage.schoolView.withinRange')}
              </p>
            )}
            <Button className="w-full justify-center" loading={update.isPending} disabled={!!error} onClick={requestSave}>{t('commissionPage.schoolView.saveChanges')}</Button>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmSave}
        onClose={() => setConfirmSave(false)}
        onConfirm={handleSave}
        title={t('commissionPage.schoolView.confirmTitle')}
        description={t('commissionPage.schoolView.confirmDescription')}
        confirmLabel={t('commissionPage.schoolView.confirmButton')}
        variant="primary"
        loading={update.isPending}
      />
    </div>
  )
}

export function CommissionPage() {
  const location = useLocation()
  // School admins get the combined Session + Certificate commission view.
  if (location.pathname.startsWith('/school')) return <SchoolCommissionView />

  return <AdminCommissionPage />
}

function AdminCommissionPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const tab = TABS.find(tb => location.pathname.includes(tb.key))?.key ?? 'trainer'
  // School admins only have Trainer + Certificate(Trainer) commission, on /school/*.
  const isSchool = location.pathname.startsWith('/school')
  const tabs = isSchool
    ? TABS.filter(tb => tb.key !== 'cert-drivys').map(tb => ({ ...tb, path: tb.path.replace('/dashboard/', '/school/') }))
    : TABS
  const isVendorView = tab === 'cert-drivys'

  const [search, setSearch] = useState('')
  const debounced = useDebounce(search)

  const trainerQ = useTrainerCommissions(!isVendorView ? { search: debounced || undefined } : undefined)
  const vendorQ  = useVendorCommissions(isVendorView ? { search: debounced || undefined } : undefined)
  const q = isVendorView ? vendorQ : trainerQ

  // trainer-certificate / trainer commission updates → set-school-trainer-commission
  const update = useUpdateCommission()
  // vendor (Drivys) certificate update → create-vendor-translation (same as old `createSchool`)
  const vendorUpdate = useMutation({
    mutationFn: (d: Record<string, unknown>) => schoolService.create(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission'] }),
  })

  const [editing, setEditing] = useState<Row | null>(null)
  const [pct, setPct] = useState('')
  const [error, setError] = useState('')
  const confirm = useConfirm()

  // For trainer-certificate edits, fetch the school to validate against its min/max bounds
  const schoolQ = useSchool(tab === 'cert-trainer' ? (editing?.vendor_id ?? 0) : 0)
  const school: any = (schoolQ.data as any)?.data ?? schoolQ.data
  const minBound = school?.min_commision ?? school?.min_commission ?? null
  const maxBound = school?.max_commision ?? school?.max_commission ?? null

  const rows: Row[] = (q.data as any)?.data ?? (q.data as any) ?? []

  const rowValue = (r: Row) =>
    tab === 'cert-drivys'  ? r.certificate_commission_in_percentage
    : tab === 'cert-trainer' ? r.trainer_certificate_commission_in_percentage
    : (r.vendor_commission_in_percentage ?? r.commission)

  const rowName = (r: Row) => r.trainer_name ?? r.vendor_name ?? r.school_name ?? r.name ?? '—'

  const openEdit = (r: Row) => {
    setEditing(r)
    setPct(String(rowValue(r) ?? ''))
    setError('')
  }

  const handlePctChange = (v: string) => {
    setPct(v)
    if (tab === 'cert-trainer' && minBound != null && maxBound != null && v !== '') {
      const n = Number(v)
      setError(n < Number(minBound) || n > Number(maxBound)
        ? t('commissionPage.adminView.commissionRangeError', { min: minBound, max: maxBound })
        : '')
    } else {
      setError('')
    }
  }

  const handleSave = async () => {
    if (!editing || error) return
    if (!(await confirm({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    const value = Number(pct)

    if (tab === 'cert-drivys') {
      // vendor certificate commission → vendor endpoint, keyed by vendor_id
      await vendorUpdate.mutateAsync({
        vendor_id: editing.vendor_id ?? editing.id,
        certificate_commission_in_percentage: value,
      })
    } else if (tab === 'cert-trainer') {
      // trainer certificate commission → keyed by mapping id
      await update.mutateAsync({
        mapping_id: editing.vendor_trainer_mapping_id,
        certificate_commission_in_percentage: value,
      })
    } else {
      // general trainer commission
      await update.mutateAsync({
        mapping_id: editing.vendor_trainer_mapping_id,
        id: editing.id,
        commission_in_percentage: value,
        vendor_commission_in_percentage: value,
      })
    }
    setEditing(null)
  }

  const saving = update.isPending || vendorUpdate.isPending
  const pct2 = (v: any) => (v == null || v === '' ? t('common.na') : `${v}%`)

  // ── Per-tab column definitions ─────────────────────────────────────────────
  const c = (k: string) => t(`commissionPage.adminView.columns.${k}`)
  const columns =
    tab === 'cert-drivys'
      ? [c('hash'), c('schoolName'), c('maxCommission'), c('minCommission'), c('drivysCertCommission'), '']
      : tab === 'cert-trainer'
        ? [c('hash'), c('trainerName'), c('schoolName'), c('trainerCertCommission'), '']
        : [c('hash'), c('trainer'), c('commissionPercent'), '']

  return (
    <div className="space-y-5">
      <PageHeader title={t('commissionPage.pageTitle')} breadcrumbs={[{ label: t('commissionPage.breadcrumb.dashboard'), to: '/dashboard' }, { label: t('commissionPage.breadcrumb.commission') }]} />
      <div className="flex border-b border-gray-100 bg-white rounded-t-xl overflow-x-auto">
        {tabs.map(tb => <NavLink key={tb.path} to={tb.path}
          className={({ isActive }) => `px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${isActive ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          {t(`commissionPage.tabs.${tb.labelKey}`)}
        </NavLink>)}
      </div>

      <Card padding={false}>
        <div className="p-5 border-b border-gray-100">
          <FilterBar search={{ value: search, onChange: setSearch, placeholder: isVendorView ? t('commissionPage.adminView.searchSchools') : t('commissionPage.adminView.searchTrainers') }} />
        </div>
        {q.isError && <p className="px-5 py-4 text-sm text-red-500">{t('commissionPage.adminView.loadFailed')}</p>}
        {q.isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24} /></div>
        ) : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr>{columns.map((col, i) => <th key={i}>{col}</th>)}</tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={columns.length} className="text-center text-gray-400 py-12 text-sm">{t('commissionPage.adminView.noRecords')}</td></tr>
              ) : rows.map(r => (
                <tr key={r.id}>
                  <td className="font-mono text-xs text-gray-400">#{r.id}</td>

                  {tab === 'cert-drivys' && <>
                    <td className="font-medium text-gray-900 cursor-pointer hover:underline"
                      onClick={() => r.vendor_id && navigate(`/dashboard/school/${r.vendor_id}`)}>{r.vendor_name ?? r.school_name ?? '—'}</td>
                    <td>{pct2(r.max_commission)}</td>
                    <td>{pct2(r.min_commission)}</td>
                    <td className="font-bold">{pct2(r.certificate_commission_in_percentage ?? 0)}</td>
                  </>}

                  {tab === 'cert-trainer' && <>
                    <td className="font-medium text-gray-900 cursor-pointer hover:underline"
                      onClick={() => r.trainer_id && navigate(isSchool ? `/school/trainer/${r.trainer_id}` : `/dashboard/user/${r.trainer_id}`)}>{r.trainer_name ?? '—'}</td>
                    <td className="cursor-pointer hover:underline"
                      onClick={() => r.vendor_id && navigate(`/dashboard/school/${r.vendor_id}`)}>{r.vendor_name ?? '—'}</td>
                    <td className="font-bold">{pct2(r.trainer_certificate_commission_in_percentage ?? 0)}</td>
                  </>}

                  {tab === 'trainer' && <>
                    <td className="font-medium text-gray-900">{rowName(r)}</td>
                    <td className="font-bold">{pct2(rowValue(r) ?? 0)}</td>
                  </>}

                  <td>
                    <button onClick={() => openEdit(r)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-50 text-brand-600 hover:bg-brand-100"><Edit size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <SlideOver open={!!editing} onClose={() => setEditing(null)} title={t('commissionPage.adminView.modalTitle')}>
        <div className="space-y-4">
          {editing && (
            <p className="text-xs text-gray-500">
              {tab === 'cert-drivys' ? (editing.vendor_name ?? editing.school_name)
                : tab === 'cert-trainer' ? `${editing.trainer_name ?? '—'} · ${editing.vendor_name ?? '—'}`
                : rowName(editing)}
            </p>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">{t('commissionPage.adminView.commissionPercentageLabel')}</label>
            <input type="number" min={0} max={100} value={pct} onChange={e => handlePctChange(e.target.value)} placeholder={t('commissionPage.adminView.commissionPlaceholder')}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${error ? 'border-red-400 focus:ring-red-300' : 'border-gray-200 focus:ring-brand-400'}`} />
            {tab === 'cert-trainer' && (
              <p className={`text-xs mt-1.5 ${error ? 'text-red-500' : 'text-gray-400'}`}>
                {error || t('commissionPage.adminView.certRangeHelp', { min: minBound != null ? `${minBound}%` : t('common.na'), max: maxBound != null ? `${maxBound}%` : t('common.na') })}
              </p>
            )}
          </div>
          <Button className="w-full justify-center" loading={saving} disabled={!!error} onClick={handleSave}>{t('common.save')}</Button>
        </div>
      </SlideOver>
    </div>
  )
}
